import React, { type ReactNode } from 'react';
import { standardizeLocale } from '@jira/platform__supported-locales/src/supported-locales.tsx';
import AnalyticsProviders from '@jira/platform__observability-analytics-providers/src/AnalyticsProviders.tsx';
import EngagementProvider from '@jira/platform__observability-engagement/src/index.tsx';
import { ExperienceTrackerProvider } from '@jira/platform__jira-experience-tracker/src/ui/experience-tracker-provider/ExperienceTrackerProvider.tsx';
import getMetaUtil from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import { IntlProvider } from '@atlassian/jira-intl/src/intl-provider.tsx';

import { ModalContextProvider } from '@jira/platform__entry-points-modal-context-provider/src/ModalContextProvider.tsx';
import { routeAnalyticsAttributes } from '@jira/platform__utils-route-analytics/src/main.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import JiraRelayEnvironmentProvider from '@jira/platform__graphql-relay-environment-provider/src/index.tsx';
import { useOrgId } from '@jira/router-resources__navigation-organization-id/src/useOrgId.tsx';
import {
	TenantContextContainer,
	TenantContextSubscriber,
} from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { LoadingPhaseManager } from './ui/loading-phase-manager/index.tsx';

export type JiraContextContainerProps = {
	children: ReactNode;
};

type State = {
	messages?: {
		[key: string]: string;
	};
};

const ContextualAnalyticsDataFeatureFlagged = ({ children }: { children: ReactNode }) => (
	<ContextualAnalyticsData attributes={routeAnalyticsAttributes}>
		{children}
	</ContextualAnalyticsData>
);

/**
 * Provides all containers and providers needed for apps that are rendered outside of the SPA context (e.g. Bento in iframe or mobile).
 */

export const JiraContextContainer = (props: JiraContextContainerProps): React.JSX.Element => {
	const state: State = {
		// oxlint-disable-next-line typescript/no-require-imports
		messages: require('language-pack'),
	};
	const { data: orgId } = useOrgId();

	return (
		<ContextualAnalyticsDataFeatureFlagged>
			<TenantContextContainer isGlobal getMeta={getMetaUtil}>
				<TenantContextSubscriber>
					{({ tenantContext }) => (
						<IntlProvider
							messages={state.messages}
							locale={standardizeLocale(tenantContext.locale)}
						>
							<AnalyticsProviders>
								<EngagementProvider orgId={orgId}>
									<ExperienceTrackerProvider>
										<JiraRelayEnvironmentProvider>
											<ModalContextProvider>{props.children}</ModalContextProvider>
										</JiraRelayEnvironmentProvider>
									</ExperienceTrackerProvider>
								</EngagementProvider>
							</AnalyticsProviders>
						</IntlProvider>
					)}
				</TenantContextSubscriber>
			</TenantContextContainer>
			<LoadingPhaseManager />
		</ContextualAnalyticsDataFeatureFlagged>
	);
};
